import React, { useState } from 'react';
import {
  Tooltip,
  Box,
  Typography,
  Divider,
  Button,
  Modal,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ITherapy } from 'interfaces/redux/ITherapy';
import { makeStyles } from '@mui/styles';
import FetchText from 'components/form/field/fetch-text';
import HTTP from 'services/http';

const useStyles = makeStyles(theme => ({
  tooltipContent: {
    padding: '16px',
    fontSize: '13px',
    lineHeight: '1.6',
    width: 'auto',
    maxWidth: '80vw',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    overflow: 'visible',
  },
  medicationItem: {
    'marginBottom': '8px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  divider: {
    margin: '8px 0',
    backgroundColor: '#ffffff',
    opacity: 0.8,
  },
  moreIndicator: {
    marginTop: '8px',
    fontStyle: 'italic',
    fontSize: '12px',
  },
  modalPaper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    padding: '24px',
    overflow: 'auto',
    boxShadow: theme.palette.mode === 'light' ? theme.shadows[5] : theme.shadows[20],
    borderRadius: '8px',
  },
  modalTitle: {
    marginBottom: '16px',
    fontWeight: 700,
    fontSize: '22px',
  },
  medicationsList: {
    maxHeight: '60vh',
    overflow: 'auto',
    marginBottom: '16px',
  },
  medicationItemInModal: {
    'paddingLeft': 0,
    'paddingRight': 0,
    'marginBottom': '12px',
    'paddingBottom': '0',
    '&:last-child': {
      marginBottom: 0,
      paddingBottom: 0,
    },
  },
  medicationName: {
    fontWeight: 500,
    fontSize: '14px',
    marginBottom: '4px',
  },
  medicationDetails: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  medicationDetailText: {
    fontSize: '14px',
    lineHeight: '1.5',
  },
  closeButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '16px',
  },
  closeButtonStyled: {
    'textTransform': 'uppercase',
    'fontSize': '14px',
    'fontWeight': 500,
    'color': theme.palette.text.primary,
    'padding': '8px 16px',
    'border': '1px solid transparent',
    'backgroundColor': 'transparent',
    'borderRadius': '4px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}));

interface MedicationInfo {
  drug_name: string;
  strength: string;
  strength_unit_of_measure: string;
  fill_cycle_number: number;
  administration_status: string;
  diagnosis_code?: string;
  diagnosis_description?: string;
  start_date?: string;
  last_csl_completed_dt?: string;
  prescribing_physician_name?: string;
  prescribing_physician_npi?: string;
  prescribing_physician_credential?: string;
  dosis_regimen?: string;
  days_supply?: number;
  therapy_id: number; // For unique key
}

interface MedicationsTooltipProps {
  medicationsList: MedicationInfo[];
  maxDisplayInTooltip?: number;
  cursor?: 'pointer' | 'default';
  showIndex?: boolean;
  children: React.ReactNode;
}

const MAX_DISPLAY_DEFAULT = 4;

function formatDate(value?: string): string {
  if (!value) {
    return '-';
  }

  const isoDate = value.split('T')[0];
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) {
    return value;
  }

  return `${month}/${day}/${year}`;
}

function medicationTitle(med: MedicationInfo, index?: number, showIndex?: boolean): string {
  const strength = med.strength ? `${med.strength}${med.strength_unit_of_measure}` : '';
  const fullName = `${med.drug_name}${strength ? ` ${strength}` : ''}`;
  const indexPrefix = index !== undefined && showIndex !== false ? `${index + 1}. ` : '';
  return `${indexPrefix}${fullName}`;
}

function medicationDetailBlock(
  med: MedicationInfo,
  index?: number,
  showIndex?: boolean,
): React.ReactElement {
  const detailSx = { mb: 0.25, fontSize: '14px', lineHeight: '1.5' };

  const renderPrescriberName = () => {
    // If we have prescriber name, use it; otherwise try to fetch via NPI
    if (med.prescribing_physician_name) {
      return (
        <>
          {med.prescribing_physician_name}
          {med.prescribing_physician_credential ? `, ${med.prescribing_physician_credential}` : ''}
          {med.prescribing_physician_npi ? ` - NPI (${med.prescribing_physician_npi})` : ''}
        </>
      );
    }

    // If no prescriber name but has NPI, fetch physician data from providers endpoint
    if (med.prescribing_physician_npi) {
      return (
        <FetchText
          promise={() => HTTP.get(`providers/physician/${med.prescribing_physician_npi}`, {})}
          parse={(result: any) => {
            const credential = result.data.credential ? `, ${result.data.credential}` : '';
            return result && result.data && typeof result.data.full_name === 'string'
              ? `${result.data.full_name} ${credential} - NPI (${med.prescribing_physician_npi})`
              : '-';
          }}
        />
      );
    }

    // Fallback if no name and no NPI
    return '-';
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, fontSize: '15px' }}>
        {medicationTitle(med, index, showIndex)}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>ICD-10:</span> {med.diagnosis_code || '-'}
        {med.diagnosis_description ? ` - ${med.diagnosis_description}` : ''}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>Start Date:</span> {formatDate(med.start_date)}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>Last CSL Completed Date:</span>{' '}
        {formatDate(med.last_csl_completed_dt)}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>Dosing Regimen:</span> {med.dosis_regimen || '-'}
      </Typography>
      <Typography variant="body2" display="block" sx={detailSx}>
        <span style={{ fontWeight: 700 }}>Prescriber:</span> {renderPrescriberName()}
      </Typography>
    </Box>
  );
}

export const MedicationsTooltip: React.FC<MedicationsTooltipProps> = ({
  medicationsList,
  maxDisplayInTooltip = MAX_DISPLAY_DEFAULT,
  cursor = 'pointer',
  showIndex = true,
  children,
}) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);

  if (!children) {
    return null;
  }

  if (!medicationsList || medicationsList.length === 0) {
    return <>{children}</>;
  }

  const displayedMeds = medicationsList.slice(0, maxDisplayInTooltip);
  const hiddenCount = medicationsList.length - maxDisplayInTooltip;
  const hasMoreMeds = hiddenCount > 0;

  const tooltipContent = (
    <Box className={classes.tooltipContent}>
      {displayedMeds.map((med, idx) => (
        <Box key={`${med.therapy_id}-${idx}`} className={classes.medicationItem}>
          {medicationDetailBlock(med, showIndex ? idx : undefined, showIndex)}
          {idx < displayedMeds.length - 1 ? <Divider className={classes.divider} /> : null}
        </Box>
      ))}
      {hasMoreMeds && (
        <>
          <Divider className={classes.divider} />
          <Box className={classes.moreIndicator}>+{hiddenCount} - Click Icon for more detaills</Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Tooltip
        title={tooltipContent}
        placement="top"
        componentsProps={{ tooltip: { sx: { maxWidth: 'none' } } }}
      >
        <Box component="div" onClick={() => setModalOpen(true)} style={{ cursor }}>
          {children}
        </Box>
      </Tooltip>

      <MedicationsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        medicationsList={medicationsList}
        showIndex={showIndex}
      />
    </>
  );
};

interface MedicationsModalProps {
  open: boolean;
  onClose: () => void;
  medicationsList: MedicationInfo[];
  showIndex?: boolean;
}

const MedicationsModal: React.FC<MedicationsModalProps> = ({
  open,
  onClose,
  medicationsList,
  showIndex,
}) => {
  const classes = useStyles();

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="medications-modal-title"
      aria-describedby="medications-modal-description"
    >
      <Paper className={classes.modalPaper}>
        <Typography id="medications-modal-title" className={classes.modalTitle}>
          Dosing Regimen FC Order ({medicationsList.length})
        </Typography>
        <Divider />

        <Box id="medications-modal-description" className={classes.medicationsList}>
          <List disablePadding>
            {medicationsList.map((med, idx) => (
              <ListItem
                key={`${med.therapy_id}-${idx}`}
                disableGutters
                className={classes.medicationItemInModal}
              >
                <ListItemText
                  primary={medicationDetailBlock(med, showIndex ? idx : undefined, showIndex)}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box className={classes.closeButton}>
          <Button className={classes.closeButtonStyled} onClick={onClose}>
            CLOSE
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default MedicationsTooltip;
